'use client';

import type { ReactNode } from 'react';
import { useState, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createSafeContext } from './createSafeContext';
import { Cookie } from '../model';
import { USER_QUERIES } from '../infrastructure';

type UserLoggedInKeyContextValue = {
  userLoggedInKey?: Cookie;
  isLoggedIn: boolean;
  setUserLoggedInKey: (value: Cookie) => void;
};

const [UserLoggedInKeyContextProvider, useUserLoggedInKeyContext] =
  createSafeContext<UserLoggedInKeyContextValue>('UserLoggedInKey');

type UserLoggedInKeyProviderProps = {
  children: ReactNode;
};

export const UserLoggedInKeyProvider = ({ children }: UserLoggedInKeyProviderProps) => {
  const [userLoggedInKeyState, setUserLoggedInKeyState] = useState<Cookie | undefined>();

  const { data: user } = useQuery(USER_QUERIES.getMe());

  const isLoggedIn = useMemo(() => {
    return Boolean(userLoggedInKeyState && user?.userId);
  }, [userLoggedInKeyState, user?.userId]);

  const setUserLoggedInKey = useCallback(({ name, value }: Cookie) => {
    setUserLoggedInKeyState((prev) => {
      return {
        ...prev,
        name,
        value,
      };
    });
  }, []);

  const contextValue = useMemo(
    () => ({
      userLoggedInKey: userLoggedInKeyState,
      isLoggedIn,
      setUserLoggedInKey,
    }),
    [userLoggedInKeyState, isLoggedIn, setUserLoggedInKey],
  );

  return <UserLoggedInKeyContextProvider value={contextValue}>{children}</UserLoggedInKeyContextProvider>;
};

export const useUserLoggedInKey = useUserLoggedInKeyContext;
